import React from 'react'
import { useAdminFilter } from './AdminFilterContext'
import { ADMIN_PRESETS, activePreset } from './dateRange'
import { DateInput, FilterBar, FilterLabel, PresetButton, Presets } from './DateRangeFilter.styles'

/** `YYYYMMDD` (API/context) → `YYYY-MM-DD` (native date input value). */
const toInputValue = (yyyymmdd: string): string =>
	`${yyyymmdd.slice(0, 4)}-${yyyymmdd.slice(4, 6)}-${yyyymmdd.slice(6, 8)}`

/** `YYYY-MM-DD` (native date input value) → `YYYYMMDD` (API/context). */
const toApiValue = (inputValue: string): string => inputValue.replace(/-/g, '')

/**
 * Global creation-date range control for the admin surface. Lives on the right of
 * the admin nav so every view shares one range via {@link useAdminFilter}. Offers
 * Today / Last 7 days / Last 30 days / All time quick-picks (the active one is
 * highlighted) plus custom From/To pickers for an arbitrary range.
 */
const DateRangeFilter: React.FC = () => {
	const { from, to, setRange } = useAdminFilter()
	const activeKey = activePreset(from, to)?.key ?? null

	return (
		<FilterBar>
			<Presets role="group" aria-label="Quick date ranges">
				{ADMIN_PRESETS.map(preset => (
					<PresetButton
						key={preset.key}
						type="button"
						$active={activeKey === preset.key}
						aria-pressed={activeKey === preset.key}
						onClick={() => {
							const range = preset.range()
							setRange(range.from, range.to)
						}}
					>
						{preset.label}
					</PresetButton>
				))}
			</Presets>
			<FilterLabel>
				From
				<DateInput
					type="date"
					aria-label="Filter from date"
					value={toInputValue(from)}
					max={toInputValue(to)}
					onChange={e => e.target.value && setRange(toApiValue(e.target.value), to)}
				/>
			</FilterLabel>
			<FilterLabel>
				To
				<DateInput
					type="date"
					aria-label="Filter to date"
					value={toInputValue(to)}
					min={toInputValue(from)}
					onChange={e => e.target.value && setRange(from, toApiValue(e.target.value))}
				/>
			</FilterLabel>
		</FilterBar>
	)
}

export default DateRangeFilter
